import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { Authenticator } from "@aws-amplify/ui-react";
import { AppThemeOptions } from "./AppThemeOptions";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const brandBlue = "#2487D8";
const brandBlueDark = "#1669ad";
const brandBlueLight = "#5aa9e8";

const hasLocalhost = (hostname: any) =>
  Boolean(
    hostname.match(/localhost/) ||
      hostname.match(/127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}/)
  );

const isLocalHost = hasLocalhost(window.location.hostname);

const darkThemeOptions = {
  ...AppThemeOptions,
  palette: {
    ...AppThemeOptions.palette,
    mode: "dark",
    primary: {
      main: brandBlue,
      light: brandBlueLight,
      dark: brandBlueDark,
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#ED7D31",
      light: "#f3a36c",
      dark: "#b95e1f",
      contrastText: "#ffffff",
    },
    success: {
      main: "#10b981",
    },
    warning: {
      main: "#f59e0b",
    },
    error: {
      main: "#fb7185",
    },
    background: {
      default: "#07131d",
      paper: "#0d141b",
    },
    text: {
      primary: "#eef5fb",
      secondary: "#96a9b8",
    },
    divider: "rgba(36, 135, 216, 0.16)",
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#07131d",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: "#0d141b",
          border: "1px solid rgba(36, 135, 216, 0.16)",
          boxShadow: "0 10px 28px rgba(0, 0, 0, 0.26)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: "#0d141b",
          border: "1px solid rgba(36, 135, 216, 0.16)",
          boxShadow: "0 10px 28px rgba(0, 0, 0, 0.26)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: 4,
          textTransform: "none",
          fontWeight: 700,
          boxShadow: "none",
          borderColor: alpha(theme.palette.primary.main, 0.38),
        }),
        contained: ({ theme }: any) => ({
          backgroundColor: theme.palette.primary.main,
          backgroundImage: "none",
          color: theme.palette.primary.contrastText,
          boxShadow: "none",
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: "none",
          },
        }),
        outlined: ({ theme }: any) => ({
          borderColor: alpha(theme.palette.primary.main, 0.36),
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            borderColor: alpha(theme.palette.primary.main, 0.62),
          },
        }),
        text: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
          backgroundColor: "transparent",
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.light,
          },
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: 4,
          margin: "1px 8px",
          color: theme.palette.text.secondary,
          "& .MuiListItemIcon-root": {
            color: theme.palette.text.secondary,
          },
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.text.primary,
            "& .MuiListItemIcon-root": {
              color: theme.palette.primary.light,
            },
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: 4,
          backgroundColor: alpha(theme.palette.common.white, 0.035),
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.primary.main, 0.18),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.primary.main, 0.45),
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: "rgba(103, 232, 249, 0.12)",
        },
        head: {
          color: "#eef5fb",
          fontWeight: 700,
          backgroundColor: "rgba(36, 135, 216, 0.08)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 700,
        },
      },
    },
  },
};

const lightThemeOptions = {
  ...AppThemeOptions,
  palette: {
    ...AppThemeOptions.palette,
    mode: "light",
    primary: {
      main: brandBlue,
      light: brandBlueLight,
      dark: brandBlueDark,
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#ED7D31",
      light: "#f3a36c",
      dark: "#b95e1f",
      contrastText: "#ffffff",
    },
    success: {
      main: "#059669",
    },
    warning: {
      main: "#d97706",
    },
    error: {
      main: "#e11d48",
    },
    background: {
      default: "#f6f9fc",
      paper: "#ffffff",
    },
    text: {
      primary: "#102026",
      secondary: "#5b6f76",
    },
    divider: "rgba(36, 135, 216, 0.16)",
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#f6f9fc",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid rgba(36, 135, 216, 0.16)",
          boxShadow: "0 8px 22px rgba(18, 54, 84, 0.08)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid rgba(36, 135, 216, 0.16)",
          boxShadow: "0 8px 22px rgba(18, 54, 84, 0.08)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 700,
          boxShadow: "none",
        },
        contained: {
          backgroundColor: brandBlue,
          backgroundImage: "none",
          color: "#ffffff",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: brandBlueDark,
            boxShadow: "none",
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: 4,
          margin: "1px 8px",
          color: theme.palette.text.secondary,
          "& .MuiListItemIcon-root": {
            color: theme.palette.text.secondary,
          },
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.text.primary,
            "& .MuiListItemIcon-root": {
              color: theme.palette.primary.main,
            },
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: 4,
          backgroundColor: alpha(theme.palette.common.white, 0.76),
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.primary.main, 0.18),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.primary.main, 0.44),
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: "rgba(36, 135, 216, 0.14)",
        },
        head: {
          color: "#102026",
          fontWeight: 700,
          backgroundColor: "rgba(36, 135, 216, 0.06)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 700,
        },
      },
    },
  },
};

const THEME_KEY = "_llmasaservice_theme";
const userThemePreference = localStorage.getItem(THEME_KEY) || "dark";

const themeOptions =
  userThemePreference === "dark" ? darkThemeOptions : lightThemeOptions;

const theme = createTheme(themeOptions as any);
const queryClient = new QueryClient();

root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        {/*<ReactQueryDevtools initialIsOpen={false} />*/}
        <BrowserRouter>
          <Authenticator.Provider>
            {/*<ReactQueryDevtools initialIsOpen={false} />*/}
            <App />
          </Authenticator.Provider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
